import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from "react-bootstrap";
import SharedForm from "../../../../components/ui/Form";
import { POSITIONS, ALIGN, YES_NO_INVERSE, FORM_ACTION_TYPE, ALERT_TYPE, CARE_GIVER_MEMBER_STATUS, VALIDATIONS, PROXY_FLAG } from "../../../../constants";
import { encodeSpecialChars, isObjectEmpty, pickProps, radioOptionValues, removeHyphens } from '../../../../utils/utils';
import messages from '../../../../utils/helper/messages';
import api from '../../../../services/api/advisoryGroupService';
import { AlertContext } from '../../../../context/AlertContext';
import withAuth from '../../../../components/hoc/withAuth';
import ConfirmModal from '../../../../components/ui/ConfirmModal';

const AddCaregiverModal = ({ show, onCancel, data, action, memberId, aGId, caregiverData }) => {

    const { showAlert } = useContext(AlertContext);

    const currentUser = useSelector((state) => state.user.currentUser);
    const proxyAttendeeDetails = useSelector(state => state.advisoryGroup.proxyAttendeeDetails);

    const [isLoading, setIsLoading] = useState(false);
    const [caregiverDetails, setCaregiverDetails] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [cloneFlag, setCloneFlag] = useState(false);

    useEffect(() => {
        if (data) {
            const editData = pickProps(data, [
                'firstName', 'lastName', 'hipaaFormFlag', 'email', 'phone', 'memberFlag', 'guardianFlag', 'hostFlag', 'comment', 'docCode'
            ]);
            setCaregiverDetails({ ...editData, docCode: editData.docCode ? editData.docCode.trim() : null });
            setCloneFlag(true);
        }
    }, [data]);

    const arr = [
        {
            key: 'firstName',
            labelName: 'First Name',
            placeholderName: 'First Name',
            type: 'text',
            valid: VALIDATIONS.String.isName,
            maxLength: 35
        },
        {
            key: 'lastName',
            labelName: 'Last Name',
            placeholderName: 'Last Name',
            type: 'text',
            valid: VALIDATIONS.String.isName,
            maxLength: 35
        },
        {
            key: 'email',
            labelName: 'Email',
            placeholderName: 'Email',
            type: 'email',
            required: false,
            maxLength: 255
        },
        {
            key: 'phone',
            labelName: 'Cellphone',
            placeholderName: 'Cell Phone',
            type: 'tel',
            maxLength: 12,
            required: false,
            valid: VALIDATIONS.Number.isPhone

        },
        {
            key: 'hipaaFormFlag',
            labelName: 'HIPAA Form Complete',
            type: 'radio',
            required: false,
            optionValues: radioOptionValues(YES_NO_INVERSE, true),
            maxLength: 12
        },
        {
            key: 'memberFlag',
            labelName: 'Company Plan Member',
            type: 'radio',
            required: false,
            optionValues: radioOptionValues(YES_NO_INVERSE, true),
            maxLength: 12,
        },
        {
            key: 'guardianFlag',
            labelName: 'Guardian Flag',
            type: 'radio',
            required: false,
            optionValues: radioOptionValues(YES_NO_INVERSE, true),
            disabled: proxyAttendeeDetails?.proxyGuardianFlag === PROXY_FLAG.Proxy
        },
        {
            key: 'docCode',
            labelName: 'Doc Code',
            placeholderName: 'Doc Code',
            type: 'text',
            required: false,
            maxLength: 4
        },
        {
            key: 'comment',
            labelName: 'Comments',
            placeholderName: 'Comments',
            type: 'textarea',
            required: false,
            valid: VALIDATIONS.String.noSpecialChars,
            maxLength: 255,
            charCounter: true
        },
    ];


    const handleSuccess = (formData) => {
        if (action === FORM_ACTION_TYPE.New) {
            const guardianData = {
                memberId: memberId,
                proxyGuardianFlag: formData.guardianFlag === YES_NO_INVERSE.No ? null : formData.guardianFlag === YES_NO_INVERSE.Yes ? PROXY_FLAG.Guardian : proxyAttendeeDetails.proxyGuardianFlag,
                guardianLink: formData.guardianFlag === YES_NO_INVERSE.No ? null : formData.guardianFlag === YES_NO_INVERSE.Yes ? formData.guardianLink : proxyAttendeeDetails.guardianLink,
                lastUsertoUpdateRecord: currentUser?.id
            };

            api.UpdateMemberGuardian(guardianData)
                .catch(handleError);
        }
        setIsLoading(false);
        showAlert(action === FORM_ACTION_TYPE.New ? messages.success.addCaregiver : messages.success.updateCaregiver, ALERT_TYPE.SUCCESS);
        onCancel();
    };

    const handleError = (error) => {
        setIsLoading(false);
        showAlert(`${messages.error.prefix} ${error}.`, ALERT_TYPE.ERROR);
    };

    const handleSubmit = (formData) => {
        setIsLoading(true);
        if (action === FORM_ACTION_TYPE.New) {
            const addData = {
                ...formData,
                companyId: currentUser?.companyId,
                agId: aGId,
                memberId: memberId,
                phone: removeHyphens(formData.phone),
                recordStatus: CARE_GIVER_MEMBER_STATUS.Operational,
                lastUserToUpdate: currentUser?.id,
                guardianFlag: formData.guardianFlag ? formData.guardianFlag : null,
                comment: encodeSpecialChars(formData.comment),
            };
            api.AddCaregiver(addData)
                .then(res => handleSuccess({ ...formData, guardianLink: res.data }))
                .catch(handleError);
        } else if (action === FORM_ACTION_TYPE.Edit) {
            const updateData = {
                ...formData,
                hipaaFormFlag: formData.hipaaFormFlag ? formData.hipaaFormFlag : null,
                memberFlag: formData.memberFlag ? formData.memberFlag : null,
                guardianFlag: formData.guardianFlag ? formData.guardianFlag : null,
                id: data.id,
                lastUserToUpdate: currentUser?.id,
                memberId: data?.memberId,
                phone: removeHyphens(formData.phone),
                comment: encodeSpecialChars(formData.comment),
            };
            const guardianData = {
                memberId: data?.memberId,
                proxyGuardianFlag: formData.guardianFlag === YES_NO_INVERSE.No ? null : formData.guardianFlag === YES_NO_INVERSE.Yes ? PROXY_FLAG.Guardian : proxyAttendeeDetails.proxyGuardianFlag,
                guardianLink: formData.guardianFlag === YES_NO_INVERSE.No ? null : formData.guardianFlag === YES_NO_INVERSE.Yes ? data?.id : proxyAttendeeDetails.guardianLink,
                lastUsertoUpdateRecord: currentUser?.id
            };

            api.UpdateCaregiver(updateData)
                .then(handleSuccess)
                .catch(handleError);
            api.UpdateMemberGuardian(guardianData)
                .catch(handleError);
        }
    };

    const isValidCaregiverInfo = () => {
        if (action === FORM_ACTION_TYPE.Edit) {
            return !isObjectEmpty(caregiverDetails);
        }
        return true;
    };

    const handleGuardianDetailsChange = (update) => setCaregiverDetails((details) => ({ ...details, ...update }));

    const handleInputChange = (name, value) => {
        const checkGuardianFlagY = caregiverData?.some(careGiver => careGiver.guardianFlag === YES_NO_INVERSE.Yes);

        if (name === 'guardianFlag' && value === YES_NO_INVERSE.Yes && checkGuardianFlagY) {
            setShowConfirmModal(true);
        }
        handleGuardianDetailsChange({ [name]: value });
    };

    const handleConfirmClose = () => setShowConfirmModal(false);

    const handleConfirm = () => {
        setShowConfirmModal(false);
    };

    return (
        <Modal
            id="add-caregiver-modal"
            show={show}
            fullscreen={false}
            backdrop="static"
            onHide={onCancel}
            size="md">
            <Modal.Header closeButton>
                <Modal.Title>
                    {action === FORM_ACTION_TYPE.New ? 'New' : 'Edit'} Caregiver
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="px-xl-3 py-xl-2 position-relative">
                    <SharedForm
                        cols={12}
                        array={arr}
                        modelObj={caregiverDetails}
                        onSubmit={handleSubmit}
                        onCancel={onCancel}
                        loading={isLoading}
                        disabled={!isValidCaregiverInfo()}
                        actionBarPosition={POSITIONS.TOP}
                        actionBarAlign={ALIGN.RIGHT}
                        onInputChanged={handleInputChange}
                        submitButtonText="Save"
                        cloneInitial={cloneFlag}
                    />
                </div>
                {showConfirmModal && (
                    <ConfirmModal
                        show={showConfirmModal}
                        message={messages.confirm.updateGuardian}
                        onConfirm={handleConfirm}
                        onCancel={handleConfirmClose}
                        onHideCallback={handleConfirmClose}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
}

export default withAuth(AddCaregiverModal);
